import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getUserOrganizations } from '@foundation/services/organizationService';
import { useUser } from '@foundation/contextapi/UserContext';
import { User, Loader2, Check, AlertCircle, ChevronsUpDown, Search, Building2 } from 'lucide-react';

type WorkspaceItem = {
  id: string;
  name: string;
  tenant_type: 'personal' | 'organization';
  role: string;
};

type OrganisationSwitcherProps = {
  onWorkspaceChange?: (workspace: WorkspaceItem) => void;
  collapsed?: boolean;
};

const STORAGE_KEY = 'revisr_active_workspace';

import { getActiveWorkspaceId } from './activeWorkspace';
export { getActiveWorkspaceId };

type DropdownRect = { top: number; left: number; width: number };

// ── Main switcher component ───────────────────────────────────────────────────

export const OrganisationSwitcher = ({ onWorkspaceChange, collapsed = false }: OrganisationSwitcherProps) => {
  const { user } = useUser();
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceItem | null>(null);
  const [logoUrls, setLogoUrls] = useState<Record<string, string | null>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onWorkspaceChangeRef = useRef(onWorkspaceChange);
  onWorkspaceChangeRef.current = onWorkspaceChange;

  // Only used as fallback when the event doesn't carry the new URL (e.g. Save Branding without logo change)
  const fetchLogoForOrg = useCallback(async (orgId: string) => {
    try {
      const { BrandingService } = await import('../foundation-overrides/services/brandingService');
      const branding = await BrandingService.getOrganizationBranding(orgId);
      const url = branding?.logo_url
        ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url)
        : null;
      setLogoUrls(prev => ({ ...prev, [orgId]: url }));
    } catch {
      // keep existing
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const loadWorkspaces = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const orgData = await getUserOrganizations();
        const items: WorkspaceItem[] = orgData.organizations.map((org: { id: string; name: string; tenant_type: string }) => {
          const membership = orgData.membership.find((m: { organisation_id: string; role: string }) => m.organisation_id === org.id);
          return {
            id: org.id,
            name: org.name,
            tenant_type: org.tenant_type === 'personal' ? 'personal' : 'organization',
            role: membership?.role || 'member',
          };
        });

        setWorkspaces(items);

        const savedId = getActiveWorkspaceId();
        const saved = items.find(w => w.id === savedId);
        const defaultWorkspace = saved || items[0] || null;

        if (defaultWorkspace) {
          setActiveWorkspace(defaultWorkspace);
          localStorage.setItem(STORAGE_KEY, defaultWorkspace.id);
          onWorkspaceChangeRef.current?.(defaultWorkspace);
        }

        // Fetch logos for all non-personal orgs — apply cache-busting so the browser
        // doesn't serve a stale image if the logo was replaced (same file path, same URL).
        const { BrandingService } = await import('../foundation-overrides/services/brandingService');
        const logoEntries = await Promise.all(
          items
            .filter(w => w.tenant_type !== 'personal')
            .map(async (w) => {
              try {
                const branding = await BrandingService.getOrganizationBranding(w.id);
                const url = branding?.logo_url
                  ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url)
                  : null;
                return { id: w.id, url };
              } catch {
                return { id: w.id, url: null };
              }
            })
        );
        setLogoUrls(Object.fromEntries(logoEntries.map((e: { id: string; url: string | null }) => [e.id, e.url])));
      } catch (err) {
        console.error('Failed to load organisations:', err);
        setError('Failed to load organisations');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkspaces();
  }, [user?.id]);

  // Listen for branding updates — update logo instantly when URL is in the event,
  // fall back to a DB fetch only when URL is not carried (Save Branding without logo change).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ orgId: string; logoUrl?: string | null }>).detail ?? {};
      const { orgId, logoUrl } = detail;
      if (!orgId) return;

      if ('logoUrl' in detail) {
        // Fast path: use URL directly from event — synchronous, zero latency
        setLogoUrls(prev => ({ ...prev, [orgId]: logoUrl ?? null }));
      } else {
        // Slow path: URL not provided, fetch from DB (uncommon case)
        fetchLogoForOrg(orgId);
      }
    };
    window.addEventListener('branding-updated', handler);
    return () => window.removeEventListener('branding-updated', handler);
  }, [fetchLogoForOrg]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(true);
  }, []);

  const handleSelect = useCallback((workspace: WorkspaceItem) => {
    setActiveWorkspace(workspace);
    localStorage.setItem(STORAGE_KEY, workspace.id);
    setIsOpen(false);
    onWorkspaceChangeRef.current?.(workspace);
    window.dispatchEvent(new CustomEvent('workspace-changed', { detail: workspace }));
  }, []);

  const personalWorkspaces = useMemo(
    () => workspaces.filter(w => w.tenant_type === 'personal'),
    [workspaces]
  );
  const orgWorkspaces = useMemo(
    () => workspaces.filter(w => w.tenant_type === 'organization'),
    [workspaces]
  );

  const totalCount = workspaces.length;

  // ── Collapsed (icon-only) mode ─────────────────────────────────────────────
  if (collapsed) {
    const isPersonal = activeWorkspace?.tenant_type === 'personal';
    const activeLogo = activeWorkspace ? logoUrls[activeWorkspace.id] ?? null : null;

    return (
      <div className="relative flex justify-center" ref={containerRef}>
        <button
          ref={triggerRef}
          onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors overflow-hidden"
          style={{
            background: 'rgba(51,184,100,0.12)',
            border: '1px solid rgba(51,184,100,0.25)',
          }}
          title={activeWorkspace?.name || 'Switch organisation'}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#33b864' }} />
          ) : activeLogo ? (
            <img src={activeLogo} alt="" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
          ) : isPersonal ? (
            <User className="w-4 h-4" style={{ color: '#33b864' }} />
          ) : (
            <span style={{ color: '#33b864', fontSize: 15, fontWeight: 700 }}>
              {activeWorkspace?.name.charAt(0).toUpperCase()}
            </span>
          )}
        </button>

        {isOpen && dropdownRect && createPortal(
          <div
            ref={dropdownRef}
            className="rounded-xl overflow-hidden"
            style={{
              position: 'fixed',
              top: dropdownRect.top,
              left: dropdownRect.left + dropdownRect.width + 8,
              width: 260,
              zIndex: 99999,
              background: '#274b3a',
              border: '1.5px solid rgba(51,184,100,0.4)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(51,184,100,0.15)',
            }}
          >
            <OrganisationDropdown
              personalWorkspaces={personalWorkspaces}
              orgWorkspaces={orgWorkspaces}
              activeWorkspace={activeWorkspace}
              logoUrls={logoUrls}
              onSelect={handleSelect}
              totalCount={totalCount}
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mx-3 mb-3">
        <div
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'rgba(255,255,255,0.4)' }} />
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="h-2.5 rounded animate-pulse w-24" style={{ background: 'rgba(255,255,255,0.1)' }} />
            <div className="h-2 rounded animate-pulse w-16" style={{ background: 'rgba(255,255,255,0.07)' }} />
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="mx-3 mb-3">
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#ef4444' }} />
          <span className="text-xs font-medium" style={{ color: '#ef4444' }}>Organisation unavailable</span>
        </div>
      </div>
    );
  }

  if (!activeWorkspace) return null;

  const isPersonal = activeWorkspace.tenant_type === 'personal';
  const activeLogo = logoUrls[activeWorkspace.id] ?? null;

  // ── Full sidebar mode ──────────────────────────────────────────────────────
  return (
    <div className="relative mx-3 mb-3" ref={containerRef}>
      <button
        ref={triggerRef}
        onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
        className="w-full flex items-center gap-2 px-2 py-2 rounded-xl transition-all duration-200 text-left group"
        style={{
          background: isOpen ? 'rgba(51,184,100,0.14)' : 'rgba(255,255,255,0.07)',
          border: isOpen
            ? '1px solid rgba(51,184,100,0.35)'
            : '1px solid rgba(255,255,255,0.1)',
          height: 54,
        }}
        aria-label="Switch organisation"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onMouseEnter={e => {
          if (!isOpen) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)';
        }}
        onMouseLeave={e => {
          if (!isOpen) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
        }}
      >
        {/* Logo — fixed 52×36px container, ~half the button width */}
        <div
          className="flex-shrink-0 flex items-center justify-center overflow-hidden rounded-md"
          style={{
            width: 68,
            height: 42,
          }}
        >
          {activeLogo ? (
            <img
              src={activeLogo}
              alt={activeWorkspace.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : isPersonal ? (
            <User className="w-4 h-4" style={{ color: '#33b864' }} />
          ) : (
            <span style={{ color: '#33b864', fontSize: 15, fontWeight: 700, letterSpacing: '-0.5px' }}>
              {activeWorkspace.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Name + type */}
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold truncate leading-tight" style={{ color: '#ffffff' }}>
            {activeWorkspace.name}
          </p>
          <p className="text-[10px] leading-tight mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {isPersonal ? 'Personal' : 'Organisation'}
          </p>
        </div>

        {/* Chevron */}
        <ChevronsUpDown
          className="w-3.5 h-3.5 flex-shrink-0 transition-colors"
          style={{ color: isOpen ? '#33b864' : 'rgba(255,255,255,0.4)' }}
        />
      </button>

      {isOpen && dropdownRect && createPortal(
        <div
          ref={dropdownRef}
          className="rounded-xl overflow-hidden"
          role="listbox"
          aria-label="Organisations"
          style={{
            position: 'fixed',
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: Math.max(dropdownRect.width, 240),
            zIndex: 99999,
            background: '#1e3a2f',
            border: '1.5px solid rgba(51,184,100,0.4)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4), 0 2px 12px rgba(51,184,100,0.18)',
          }}
        >
          <OrganisationDropdown
            personalWorkspaces={personalWorkspaces}
            orgWorkspaces={orgWorkspaces}
            activeWorkspace={activeWorkspace}
            logoUrls={logoUrls}
            onSelect={handleSelect}
            totalCount={totalCount}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Shared dropdown content ────────────────────────────────────────────────────

type OrganisationDropdownProps = {
  personalWorkspaces: WorkspaceItem[];
  orgWorkspaces: WorkspaceItem[];
  activeWorkspace: WorkspaceItem | null;
  logoUrls: Record<string, string | null>;
  onSelect: (workspace: WorkspaceItem) => void;
  totalCount: number;
};

const OrganisationDropdown = ({
  personalWorkspaces,
  orgWorkspaces,
  activeWorkspace,
  logoUrls,
  onSelect,
  totalCount,
}: OrganisationDropdownProps) => {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const showSearch = totalCount > 3;

  // Auto-focus search when dropdown opens
  useEffect(() => {
    if (showSearch) setTimeout(() => searchRef.current?.focus(), 50);
  }, [showSearch]);

  const q = search.toLowerCase().trim();

  const filteredPersonal = q
    ? personalWorkspaces.filter(w => w.name.toLowerCase().includes(q))
    : personalWorkspaces;

  const filteredOrgs = q
    ? orgWorkspaces.filter(w => w.name.toLowerCase().includes(q))
    : orgWorkspaces;

  const totalFiltered = filteredPersonal.length + filteredOrgs.length;

  return (
    <>
      {/* Header */}
      <div
        className="px-3 py-2.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}
      >
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#33b864' }}>
          Switch Organisation
        </p>
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{ background: 'rgba(51,184,100,0.15)', color: '#33b864' }}
        >
          {totalCount}
        </span>
      </div>

      {/* Search — shown when more than 3 orgs */}
      {showSearch && (
        <div className="px-2.5 pt-2 pb-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search organisations…"
              className="w-full pl-8 pr-10 py-1.5 text-xs rounded-lg outline-none"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#ffffff',
              }}
            />
            {search ? (
              <span
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold"
                style={{ color: 'rgba(51,184,100,0.8)' }}
              >
                {totalFiltered}
              </span>
            ) : (
              <span
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px]"
                style={{ color: 'rgba(255,255,255,0.25)' }}
              >
                {totalCount}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Scrollable list */}
      <div style={{ maxHeight: 380, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(51,184,100,0.3) transparent' }}>
        {/* No results */}
        {totalFiltered === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
            <Building2 className="w-6 h-6" style={{ color: 'rgba(255,255,255,0.2)' }} />
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              No organisations match "{search}"
            </p>
          </div>
        )}

        {/* Personal workspaces */}
        {filteredPersonal.length > 0 && (
          <div className="py-1">
            <p
              className="px-3 pt-2 pb-0.5 text-[9px] font-bold uppercase tracking-widest"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              Personal
            </p>
            {filteredPersonal.map(workspace => (
              <OrganisationOption
                key={workspace.id}
                workspace={workspace}
                isActive={activeWorkspace?.id === workspace.id}
                logoUrl={null}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {/* Organisations */}
        {filteredOrgs.length > 0 && (
          <div
            className="py-1"
            style={filteredPersonal.length > 0 ? { borderTop: '1px solid rgba(255,255,255,0.08)' } : undefined}
          >
            {filteredPersonal.length > 0 && (
              <p
                className="px-3 pt-2 pb-0.5 text-[9px] font-bold uppercase tracking-widest"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                Organisations
              </p>
            )}
            {filteredOrgs.map(workspace => (
              <OrganisationOption
                key={workspace.id}
                workspace={workspace}
                isActive={activeWorkspace?.id === workspace.id}
                logoUrl={logoUrls[workspace.id] ?? null}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ── Single organisation option ─────────────────────────────────────────────────

type OrganisationOptionProps = {
  workspace: WorkspaceItem;
  isActive: boolean;
  logoUrl: string | null;
  onSelect: (workspace: WorkspaceItem) => void;
};

const OrganisationOption = ({ workspace, isActive, logoUrl, onSelect }: OrganisationOptionProps) => {
  const isPersonal = workspace.tenant_type === 'personal';
  const roleLabel = workspace.role === 'admin' || workspace.role === 'org_admin' ? 'Admin' : 'Member';
  const initial = workspace.name.charAt(0).toUpperCase();

  return (
    <button
      onClick={() => onSelect(workspace)}
      role="option"
      aria-selected={isActive}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-all duration-150"
      style={{
        background: isActive ? 'rgba(51,184,100,0.18)' : 'transparent',
        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.8)',
      }}
      onMouseEnter={e => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
          (e.currentTarget as HTMLElement).style.color = '#ffffff';
        }
      }}
      onMouseLeave={e => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
          (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.8)';
        }
      }}
    >
      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{
          background: logoUrl ? '#ffffff' : (isActive ? 'rgba(51,184,100,0.25)' : 'rgba(255,255,255,0.1)'),
          border: logoUrl ? '1px solid rgba(229,231,235,0.5)' : 'none',
        }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={workspace.name} style={{ width: '86%', height: '86%', objectFit: 'contain' }} />
        ) : isPersonal ? (
          <User className="w-3.5 h-3.5" style={{ color: isActive ? '#33b864' : 'rgba(255,255,255,0.6)' }} />
        ) : (
          <span style={{ color: isActive ? '#33b864' : 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 700 }}>
            {initial}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold truncate leading-tight" style={{ color: '#ffffff' }}>
          {workspace.name}
        </p>
        <p className="text-[10px] leading-tight" style={{ color: '#9ca3af' }}>{roleLabel}</p>
      </div>

      {isActive && (
        <div
          className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: '#33b864' }}
        >
          <Check className="w-2.5 h-2.5 text-white" />
        </div>
      )}
    </button>
  );
};

// Keep backward-compatible export alias
export const WorkspaceSwitcher = OrganisationSwitcher;
